import { Children, Fragment, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { Info, Minus, Plus } from '@digital-twins/design-system/icons'
import { CreditIcon } from '@/components/icons/CreditIcon'
import {
  ASSISTANT_PRICE_LEVELS,
  PRIVATE_CREDIT_RATES,
  SOCIAL_LISTENING_CREDIT_RATE,
  assistantRateRows,
  roomRateRows,
} from '@/lib/billing/creditRateTable'
import {
  ASSISTANT_IMAGE_CREDITS,
  SOCIAL_POSTS_PER_CREDIT_BLOCK,
  socialCreditsForPosts,
} from '@digital-twins/shared-types'
import { useMeteringEnrolled } from '@/lib/billing/metering'
import { formatCreditAmount } from '@/lib/billing/formatCreditAmount'

import type { ReactNode } from 'react'
import type { BillingInterval } from '@/lib/api'
import type { ConversationLevel } from '@digital-twins/shared-types'

// ─── rates ───────────────────────────────────────────────────────────────────

// Every row prices off the shared price list the credits dialog renders, so the two
// surfaces never quote different numbers and no rate is inlined here as a literal.
function rateFor(action: string): number {
  const row = PRIVATE_CREDIT_RATES.find((r) => r.action === action)
  if (!row) {
    throw new Error(`Rate missing from PRIVATE_CREDIT_RATES: ${action}`)
  }
  return row.rate
}

const STUDY_RATE = rateFor('Study')
const AUDIENCE_RATE = rateFor('Audience creation')
const QUICK_STUDY_RATE = rateFor('Quick study')
const CHAT_RATE = rateFor('Chat')

// Level-dependent rates (credit metering): the Assistant's typical credits per message and
// the enrolled 1:1 chat price, both per level.
const ASSISTANT_RATE_BY_LEVEL = Object.fromEntries(
  assistantRateRows().map((row) => [row.level, row.typicalCreditsPerMessage]),
) as Record<ConversationLevel, number>
const CHAT_RATE_BY_LEVEL = roomRateRows().find((row) => row.kind === 'chat')?.byLevel

const SOCIAL_RATE = SOCIAL_LISTENING_CREDIT_RATE.rate
const SOCIAL_BLOCK = SOCIAL_POSTS_PER_CREDIT_BLOCK
const DEFAULT_LEVEL: ConversationLevel = 'medium'
const IMAGE_QUALITIES = ['medium', 'high'] as const
type ImageQuality = (typeof IMAGE_QUALITIES)[number]

// Desktop: label | inputs | total in fixed columns so every total lines up right after the
// inputs (md and up). Narrower: label and total on the first line, inputs on the second.
const ROW_GRID =
  'grid grid-cols-[minmax(0,1fr)_auto] md:grid-cols-[7.5rem_minmax(0,25rem)_5rem] md:justify-start items-center gap-x-2 gap-y-1.5'

// ─── types ───────────────────────────────────────────────────────────────────

export interface CreditScenarioModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Kept for callers; the estimator shows credits only, so the interval does not change it. */
  interval?: BillingInterval | null
  /** Credit metering enrollment; defaults to the signed-in account's own mode. */
  enrolled?: boolean
}

// ─── stepper ─────────────────────────────────────────────────────────────────

interface StepperProps {
  value: number
  onChange: (v: number) => void
  min: number
  max: number
  step?: number
  /** Accessible name of the number and the muted unit after it, e.g. "twins". */
  unit: string
  testId: string
}

function Stepper(props: StepperProps) {
  const { value, onChange, min, max, step = 1, unit, testId } = props
  const { t } = useTranslation('app')

  const [draft, setDraft] = useState(String(value))

  useEffect(() => {
    setDraft(String(value))
  }, [value])

  const commit = () => {
    const n = parseInt(draft, 10)
    if (Number.isNaN(n)) {
      setDraft(String(value))
      return
    }
    const clamped = Math.min(max, Math.max(min, n))
    setDraft(String(clamped))
    onChange(clamped)
  }

  const buttonClass =
    'inline-flex h-full w-5 shrink-0 items-center justify-center text-neutral-500 hover:bg-neutral-100 hover:text-neutral-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-30'

  return (
    <span className="inline-flex shrink-0 items-center gap-1 whitespace-nowrap" data-testid={testId}>
      <span className="inline-flex h-7 items-stretch overflow-hidden rounded-md border border-neutral-200 bg-white">
        <button
          type="button"
          onClick={() => onChange(Math.max(min, value - step))}
          disabled={value <= min}
          aria-label={t('billing.estimator.decrease', { label: unit })}
          className={`${buttonClass} border-r border-neutral-200`}
          data-testid={`${testId}-decrease`}
        >
          <Minus className="h-3 w-3" />
        </button>
        <input
          type="number"
          inputMode="numeric"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              commit()
            }
          }}
          min={min}
          max={max}
          step={step}
          aria-label={unit}
          style={{ width: `${String(max).length + 1}ch` }}
          className="bg-transparent text-center text-sm tabular-nums text-neutral-900 outline-none focus-visible:bg-neutral-100 [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
          data-testid={`${testId}-input`}
        />
        <button
          type="button"
          onClick={() => onChange(Math.min(max, value + step))}
          disabled={value >= max}
          aria-label={t('billing.estimator.increase', { label: unit })}
          className={`${buttonClass} border-l border-neutral-200`}
          data-testid={`${testId}-increase`}
        >
          <Plus className="h-3 w-3" />
        </button>
      </span>
      <span className="text-xs text-neutral-500" aria-hidden="true">{unit}</span>
    </span>
  )
}

// ─── level select ────────────────────────────────────────────────────────────

interface LevelSelectProps {
  value: ConversationLevel
  onChange: (level: ConversationLevel) => void
  label: string
  testId: string
}

function LevelSelect(props: LevelSelectProps) {
  const { value, onChange, label, testId } = props
  const { t } = useTranslation('app')

  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value as ConversationLevel)}
      aria-label={t('billing.estimator.levelLabel', { label })}
      className="h-7 shrink-0 rounded-md border border-neutral-200 bg-white px-1.5 text-xs text-neutral-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      data-testid={testId}
    >
      {ASSISTANT_PRICE_LEVELS.map((level) => (
        <option key={level} value={level} data-testid={`${testId}-${level}`}>
          {t(`assistantProfile.${level}`)}
        </option>
      ))}
    </select>
  )
}

interface ImageQualitySelectProps {
  value: ImageQuality
  onChange: (quality: ImageQuality) => void
  label: string
  testId: string
}

function ImageQualitySelect(props: ImageQualitySelectProps) {
  const { value, onChange, label, testId } = props
  const { t } = useTranslation('app')

  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value as ImageQuality)}
      aria-label={t('billing.estimator.levelLabel', { label })}
      className="h-7 shrink-0 rounded-md border border-neutral-200 bg-white px-1.5 text-xs text-neutral-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      data-testid={testId}
    >
      {IMAGE_QUALITIES.map((quality) => (
        <option key={quality} value={quality} data-testid={`${testId}-${quality}`}>
          {t(`assistantProfile.${quality}`)}
        </option>
      ))}
    </select>
  )
}

// ─── row ─────────────────────────────────────────────────────────────────────

interface EstimateRowProps {
  label: string
  rate: string
  credits: number
  testId: string
  children: ReactNode
}

function EstimateRow(props: EstimateRowProps) {
  const { label, rate, credits, testId, children } = props
  const { t } = useTranslation('app')

  const factors = Children.toArray(children)

  return (
    <div
      role="group"
      aria-label={label}
      className={`${ROW_GRID} min-h-11 border-b border-neutral-200 py-1.5`}
      data-testid={testId}
    >
      <div className="col-start-1 row-start-1 flex min-w-0 items-center gap-1">
        <span className="truncate text-sm text-neutral-900">{label}</span>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              aria-label={t('billing.estimator.rateInfo', { label })}
              className="shrink-0 rounded-full p-0.5 text-neutral-500 hover:text-neutral-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              data-testid={`${testId}-info`}
            >
              <Info className="h-3.5 w-3.5" aria-hidden />
            </button>
          </TooltipTrigger>
          <TooltipContent side="top" className="text-xs" data-testid={`${testId}-rate`}>
            <span className="inline-flex items-center gap-1">
              <CreditIcon className="h-3 w-3 shrink-0" />
              {rate}
            </span>
          </TooltipContent>
        </Tooltip>
      </div>
      <div className="col-span-2 row-start-2 flex min-w-0 flex-wrap items-center gap-x-1 gap-y-1 md:col-span-1 md:col-start-2 md:row-start-1 md:flex-nowrap">
        {factors.map((factor, index) => (
          <Fragment key={index}>
            {index > 0 ? <span className="text-xs text-neutral-500" aria-hidden="true">×</span> : null}
            {factor}
          </Fragment>
        ))}
      </div>
      <span
        className="col-start-2 row-start-1 whitespace-nowrap text-right font-display text-lg font-normal tabular-nums text-neutral-900 md:col-start-3"
        data-testid={`${testId}-credits`}
      >
        <span className="mr-1 text-xs text-neutral-500" aria-hidden="true">=</span>
        {credits.toLocaleString()}
      </span>
    </div>
  )
}

// ─── modal ───────────────────────────────────────────────────────────────────

export function CreditScenarioModal(props: CreditScenarioModalProps) {
  const { open, onOpenChange, enrolled: enrolledProp } = props
  const { t } = useTranslation('app')

  const accountEnrolled = useMeteringEnrolled()
  const [studies, setStudies] = useState(2)
  const [sampleSize, setSampleSize] = useState(100)
  const [audiences, setAudiences] = useState(1)
  const [audienceSize, setAudienceSize] = useState(500)
  const [quickStudies, setQuickStudies] = useState(4)
  const [quickQuestions, setQuickQuestions] = useState(10)
  const [quickTwins, setQuickTwins] = useState(100)
  const [chatSessions, setChatSessions] = useState(10)
  const [msgsPerSession, setMsgsPerSession] = useState(10)
  const [chatLevel, setChatLevel] = useState<ConversationLevel>(DEFAULT_LEVEL)
  const [assistantMessages, setAssistantMessages] = useState(100)
  const [assistantLevel, setAssistantLevel] = useState<ConversationLevel>(DEFAULT_LEVEL)
  const [imageCount, setImageCount] = useState(10)
  const [imageQuality, setImageQuality] = useState<ImageQuality>('medium')
  const [socialPosts, setSocialPosts] = useState(SOCIAL_BLOCK * 10)

  const isEnrolled = enrolledProp ?? accountEnrolled
  // Chats are level-priced only for enrolled accounts on a price list with level rates.
  const chatLevelRates = isEnrolled ? CHAT_RATE_BY_LEVEL : undefined
  const chatRate = chatLevelRates ? chatLevelRates[chatLevel] : CHAT_RATE
  const assistantRate = ASSISTANT_RATE_BY_LEVEL[assistantLevel]
  const imageRate = ASSISTANT_IMAGE_CREDITS[imageQuality]

  const breakdown = useMemo(() => {
    const studyCredits = studies * sampleSize * STUDY_RATE
    const audienceCredits = audiences * audienceSize * AUDIENCE_RATE
    // Rounded before the total reads it: a typed value can bypass the twins step of 10.
    const quickStudyCredits = Math.round(quickStudies * quickQuestions * quickTwins * QUICK_STUDY_RATE)
    const chatCredits = Math.round(chatSessions * msgsPerSession * chatRate)
    // The Assistant estimated by typical messages at the chosen level, for every account.
    const assistantCredits = Math.round(assistantMessages * assistantRate)
    const imageCredits = Math.round(imageCount * imageRate)
    // The price owner's own formula, so the estimate matches the real charge.
    const socialCredits = socialCreditsForPosts(socialPosts)
    const total =
      studyCredits + audienceCredits + quickStudyCredits + chatCredits + assistantCredits + imageCredits + socialCredits
    return { studyCredits, audienceCredits, quickStudyCredits, chatCredits, assistantCredits, imageCredits, socialCredits, total }
  }, [studies, sampleSize, audiences, audienceSize, quickStudies, quickQuestions, quickTwins,
      chatSessions, msgsPerSession, chatRate, isEnrolled, assistantMessages, assistantRate, imageCount, imageRate, socialPosts])

  const units = {
    studies: t('billing.estimator.unitStudies'),
    audiences: t('billing.estimator.unitAudiences'),
    twins: t('billing.estimator.unitTwins'),
    questions: t('billing.estimator.unitQuestions'),
    chats: t('billing.estimator.unitChats'),
    messages: t('billing.estimator.unitMessages'),
    posts: t('billing.estimator.unitPosts'),
    images: t('billing.estimator.unitImages'),
  }
  const labels = {
    studies: t('billing.estimator.studies'),
    audiences: t('billing.estimator.audiences'),
    quickStudies: t('billing.estimator.quickStudies'),
    chats: t('billing.estimator.chats'),
    assistant: t('billing.estimator.assistant'),
    images: t('billing.estimator.images'),
    social: t('billing.estimator.social'),
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-white text-neutral-900 border-neutral-200 [color-scheme:light] max-w-[min(42rem,calc(100%-32px))] rounded-2xl p-0 sm:p-0 gap-0"
        data-testid="credit-scenario-modal"
      >
        <TooltipProvider delayDuration={100}>
          <div className="min-w-0 px-4 py-5 sm:px-5">
            <DialogTitle className="font-display text-xl font-normal tracking-tight text-neutral-900">
              {t('billing.estimateUsage')}
            </DialogTitle>
            <DialogDescription className="mt-1 mb-3 text-xs text-neutral-500">
              {t('billing.estimator.description')}
            </DialogDescription>

            <div className="border-t border-neutral-200">
              <EstimateRow
                label={labels.studies}
                rate={t('billing.estimator.ratePerTwin', { value: formatCreditAmount(STUDY_RATE) })}
                credits={breakdown.studyCredits}
                testId="credit-scenario-study-row"
              >
                <Stepper value={studies} onChange={setStudies} min={1} max={20} unit={units.studies} testId="credit-scenario-studies" />
                <Stepper value={sampleSize} onChange={setSampleSize} min={50} max={2000} step={50} unit={units.twins} testId="credit-scenario-study-twins" />
              </EstimateRow>

              <EstimateRow
                label={labels.audiences}
                rate={t('billing.estimator.ratePerTwin', { value: formatCreditAmount(AUDIENCE_RATE) })}
                credits={breakdown.audienceCredits}
                testId="credit-scenario-audience-row"
              >
                <Stepper value={audiences} onChange={setAudiences} min={0} max={10} unit={units.audiences} testId="credit-scenario-audiences" />
                <Stepper value={audienceSize} onChange={setAudienceSize} min={50} max={5000} step={50} unit={units.twins} testId="credit-scenario-audience-twins" />
              </EstimateRow>

              {/* Twins step by 10 so rate x twins stays whole, matching the real per-question charge. */}
              <EstimateRow
                label={labels.quickStudies}
                rate={t('billing.estimator.ratePerRead', { value: formatCreditAmount(QUICK_STUDY_RATE) })}
                credits={breakdown.quickStudyCredits}
                testId="credit-scenario-quick-study"
              >
                <Stepper value={quickStudies} onChange={setQuickStudies} min={0} max={20} unit={units.studies} testId="credit-scenario-quick-studies" />
                <Stepper value={quickQuestions} onChange={setQuickQuestions} min={1} max={30} unit={units.questions} testId="credit-scenario-quick-questions" />
                <Stepper value={quickTwins} onChange={setQuickTwins} min={10} max={2000} step={10} unit={units.twins} testId="credit-scenario-quick-twins" />
              </EstimateRow>

              <EstimateRow
                label={labels.chats}
                rate={t('billing.estimator.ratePerMessage', { value: formatCreditAmount(chatRate) })}
                credits={breakdown.chatCredits}
                testId="credit-scenario-chat-row"
              >
                <Stepper value={chatSessions} onChange={setChatSessions} min={0} max={50} unit={units.chats} testId="credit-scenario-chats" />
                <Stepper value={msgsPerSession} onChange={setMsgsPerSession} min={1} max={30} unit={units.messages} testId="credit-scenario-chat-messages" />
                {chatLevelRates ? (
                  <LevelSelect value={chatLevel} onChange={setChatLevel} label={labels.chats} testId="credit-scenario-chat-level" />
                ) : null}
              </EstimateRow>

              <EstimateRow
                label={labels.assistant}
                rate={t('billing.estimator.rateTypicalMessage', { value: formatCreditAmount(assistantRate) })}
                credits={breakdown.assistantCredits}
                testId="credit-scenario-assistant-row"
              >
                <Stepper value={assistantMessages} onChange={setAssistantMessages} min={0} max={2000} step={10} unit={units.messages} testId="credit-scenario-assistant-messages" />
                <LevelSelect value={assistantLevel} onChange={setAssistantLevel} label={labels.assistant} testId="credit-scenario-assistant-level" />
              </EstimateRow>

              <EstimateRow
                label={labels.images}
                rate={t('billing.estimator.rateImage', { value: formatCreditAmount(imageRate) })}
                credits={breakdown.imageCredits}
                testId="credit-scenario-image-row"
              >
                <Stepper value={imageCount} onChange={setImageCount} min={0} max={500} step={5} unit={units.images} testId="credit-scenario-images" />
                <ImageQualitySelect value={imageQuality} onChange={setImageQuality} label={labels.images} testId="credit-scenario-image-quality" />
              </EstimateRow>

              <EstimateRow
                label={labels.social}
                rate={t('billing.estimator.ratePerPosts', { value: formatCreditAmount(SOCIAL_RATE), posts: SOCIAL_BLOCK })}
                credits={breakdown.socialCredits}
                testId="credit-scenario-social-row"
              >
                <Stepper value={socialPosts} onChange={setSocialPosts} min={0} max={SOCIAL_BLOCK * 100} step={SOCIAL_BLOCK} unit={units.posts} testId="credit-scenario-social-posts" />
              </EstimateRow>
            </div>

            <div className={`${ROW_GRID} pt-3`}>
              <span className="col-start-1 text-sm text-neutral-500 md:col-start-2 md:text-right">
                {t('billing.estimator.total')}
              </span>
              <span
                className="col-start-2 whitespace-nowrap text-right font-display text-3xl font-normal tabular-nums text-neutral-900 md:col-start-3"
                data-testid="credit-scenario-total"
              >
                {breakdown.total.toLocaleString()}
              </span>
            </div>

            <p className="mt-4 text-xs leading-relaxed text-neutral-500" data-testid="credit-scenario-footnote">
              {t('billing.plans.marketplaceFootnote')}
            </p>
          </div>
        </TooltipProvider>
      </DialogContent>
    </Dialog>
  )
}
